<template>
  <div v-if="!flagsResolved"><slot name="loading"></slot></div>
  <div v-else-if="featureEnabled">
    <slot name="featureEnabled"><slot></slot></slot>
  </div>
  <div v-else><slot name="featureDisabled"></slot></div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { getProperty } from "@/lib/helpers.js";

// Three render states, not two. `featureEnabled` cannot distinguish "flag is
// off" from "flags have not loaded yet" — both are false — and `created()`
// starts the tenant fetch, so without a gate every consumer is guaranteed a
// disabled-branch first paint that is then torn down.
//
// That is not cosmetic: the disabled branch MOUNTS, so its side effects run.
// Where the two branches are two implementations of the same table, the legacy
// one fires a full server-side query, and the replacement fires the identical
// query again a few hundred milliseconds later. Gating on `flagsResolved`
// renders neither branch until the answer is actually known.
export default {
  props: {
    feature: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      // Set once the tenant fetch has SETTLED, success or failure. Deliberately
      // separate from `tenant` being populated: `fetchTenant` has no `.catch()`,
      // so on a rejected request `tenant` stays null forever. Without this flag
      // the gate would never open and the consumer would render nothing at all.
      fetchSettled: false
    };
  },
  computed: {
    ...mapState({
      tenant: "tenant",
      fetchingTenant: "fetchingTenant",
      featureEnabled({ tenant }) {
        if (!getProperty(tenant, "feature_flags") || !this.feature) {
          return false;
        }
        return tenant.feature_flags[this.feature];
      }
    }),
    // Unresolved means "we cannot yet tell on from off". Once flags are present
    // the answer is authoritative; once the fetch has settled without them
    // (failure, or a tenant payload with no feature_flags key) the answer is
    // "off", which is the pre-existing behaviour.
    flagsResolved() {
      return !!getProperty(this.tenant, "feature_flags") || this.fetchSettled;
    }
  },
  watch: {
    fetchingTenant(isFetching, wasFetching) {
      // `fetchTenant` clears this in a `finally`, so it flips back to false on
      // both the success and the failure path. That makes this the one signal
      // that reliably marks the fetch as settled, including for instances that
      // did not start the request themselves.
      if (wasFetching && !isFetching) {
        this.fetchSettled = true;
      }
    }
  },
  created() {
    if (this.tenant) {
      // Tenant already in the store: whatever flags it carries are final.
      this.fetchSettled = true;
    } else if (!this.fetchingTenant) {
      this.fetchTenant();
    }
    // else: a fetch is already in flight (started by another FeatureFlag or by
    // useFeatureFlag); the `fetchingTenant` watcher resolves this instance.
  },
  methods: {
    ...mapActions(["fetchTenant"])
  }
};
</script>

<style lang="scss" scoped></style>
